import ReviewsCarousel from './ReviewsCarousel';
import { Fragment } from "react";
import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";
import CodeIcon from "@mui/icons-material/Code";
import StorageIcon from "@mui/icons-material/Storage";
import HubIcon from "@mui/icons-material/Hub";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ScheduleIcon from "@mui/icons-material/Schedule";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import LeadForm from "./LeadForm";
import PageMeta from "./PageMeta";
import {
  RedesignFooter,
  RedesignHeader,
  RedesignPage,
  SiteLink,
} from "./RedesignShell";
import mobile from "./mobile/mobile.json";
import mvp from "./mobile/mvp.json";
import ios from "./mobile/ios.json";
import android from "./mobile/android.json";
import integrations from "./mobile/integrations.json";
import publishing from "./mobile/publishing.json";
import googlePlay from "./mobile/google-play.json";
import appStore from "./mobile/app-store.json";
import rustore from "./mobile/rustore.json";
import aso from "./mobile/aso.json";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import "./mobile-development.css";

// Источники: .planning/content/mobile/*.md. Структура каждого ТЗ сохранена в mobile/*.json:
// [meta] title/description; [H1/intro]; [TOC из H2]; 16 секций H2 с H3, таблицами,
// списками, отзывами, FAQ; формы в оценке стоимости, после отзывов и финальном CTA.
export const mobilePages = { mobile, mvp, ios, android, integrations, publishing, "google-play": googlePlay, "app-store": appStore, rustore, aso };
const plainText = (value) =>
  value.replace(/\*\*/g, "").replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
Object.values(mobilePages).forEach((page) => {
  const faq = page.sections.find(
    (section) => section.title === "Ответы на частые вопросы",
  );
  page.structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: page.h1,
      description: page.description,
      url: `https://fatalitystudio.ru${page.path}`,
      provider: { "@type": "Organization", name: "Fatality Studio" },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: splitGroups(faq.blocks).groups.map((group) => ({
        "@type": "Question",
        name: group.title,
        acceptedAnswer: {
          "@type": "Answer",
          text: group.blocks
            .map((block) => plainText(block.text || ""))
            .join(" "),
        },
      })),
    },
  ];
});
const relatedPaths = {
  "Публикация в RuStore": "/services/mobile/publishing/rustore/",
  "Публикация в App Store": "/services/mobile/publishing/app-store/",
  "Публикация в Google Play": "/services/mobile/publishing/google-play/",
  "Публикация приложений": "/services/mobile/publishing/",
  "Разработка приложений для Android": "/services/mobile/android/",
  "Разработка приложений для iOS": "/services/mobile/ios/",
  "Разработка приложений": "/services/mobile/",
  "Разработка MVP": "/services/mobile/mvp/",
  "Разработка Mini App": "/services/mini-apps/",
  "Разработка сайтов": "/services/websites/",
  "Разработка сайтов под ключ": "/services/websites/",
  "Разработка лендинга": "https://fatalitystudio.ru/services/websites/landing/",
  "SEO-продвижение сайтов": "/services/seo/",
  "Продвижение в Яндекс.Картах": "/services/yandex-maps/",
};
const typeLinks = {
  "Мобильные приложения для iOS и Android": "/services/mobile/ios/",
  "Приложения для интернет-магазинов": "/services/mobile/ecommerce/",
  "Корпоративные приложения": "/services/mobile/corporate/",
  "Приложения для ресторанов, доставки и сервисов":
    "/services/mobile/delivery/",
  "Редизайн и доработка существующих приложений":
    "/services/mobile/redesign-support/",
};

// Без HTML из документа: только типографическое выделение и ссылки React.
export function MobileInline({ text }) {
  return String(text)
    .split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g)
    .map((part, index) => {
      if (part.startsWith("**"))
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (link && /^(https?:\/\/|\/|#)/.test(link[2]))
        return (
          <SiteLink key={index} href={link[2]}>
            {link[1]}
          </SiteLink>
        );
      return <Fragment key={index}>{part}</Fragment>;
    });
}

function Block({ block }) {
  if (block.type === "list")
    return (
      <ul>
        {block.items.map((text, i) => (
          <li key={i}>
            <MobileInline text={text} />
          </li>
        ))}
      </ul>
    );
  if (block.type === "table")
    return (
      <div
        className="mobile-table-scroll"
        role="region"
        aria-label={block.rows[0]
          .map((x) => x.replaceAll("**", ""))
          .join(" / ")}
        tabIndex="0"
      >
        <table>
          <thead>
            <tr>
              {block.rows[0].map((text, i) => (
                <th key={i} scope="col">
                  <MobileInline text={text} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.slice(1).map((row, i) => (
              <tr key={i}>
                {row.map((text, j) => (
                  <td key={j}>
                    <MobileInline text={text} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  return (
    <p
      className={
        block.text.startsWith("**Важно!**") ? "mobile-important" : undefined
      }
    >
      <MobileInline text={block.text} />
    </p>
  );
}

function splitGroups(blocks) {
  const intro = [],
    groups = [];
  let target = intro;
  blocks.forEach((block) => {
    if (block.type === "h3") {
      const group = { title: block.text, blocks: [] };
      groups.push(group);
      target = group.blocks;
    } else target.push(block);
  });
  return { intro, groups };
}

function ProjectForm({ page, slot, final = false }) {
  const isMvp = page.key === "mvp";
  const isIntegration = page.key === "integrations";
  const isPublishing = ["publishing", "google-play", "app-store", "rustore", "aso"].includes(page.key);
  const options = page.ui?.typeOptions || (isIntegration
    ? ["Платежная система", "CRM, ERP или учетная система", "Мобильное приложение и сайт", "Внешние сервисы", "Несколько систем"]
    : isMvp
    ? [
        "MVP мобильного приложения",
        "MVP веб-сервиса и платформы",
        "MVP на базе Telegram Mini App и ботов",
        "MVP под нестандартные задачи",
      ]
    : [
        "Мобильное приложение",
        "Интернет-магазин",
        "Корпоративное приложение",
        "Ресторан, доставка или сервис",
        "Редизайн и доработка",
      ]);
  return (
    <div
      className="mobile-form-wrap"
      id={slot === "final" ? `${page.key}-lead` : undefined}
    >
      {final && (
        <>
          <h3>{page.ui?.finalHeading || "Получите расчет стоимости за 1 день"}</h3>
          <a className="mobile-phone" href="tel:+79256530454">
            +7 (925) 653-04-54
          </a>
        </>
      )}
      <LeadForm
        formId={`${page.key}-${slot}-form`}
        idPrefix={`${page.key}-${slot}`}
        showCompany={false}
        showTelegram={false}
        contactLabel="Телефон или E-mail"
        contactType="text"
        contactPlaceholder="+7 (___) ___-__-__ или name@example.ru"
        typeLabel={page.ui?.typeLabel || (isIntegration ? (final ? "Какие системы связать" : "Какие системы нужно связать") : isMvp ? "Тип MVP" : "Тип приложения")}
        typeOptions={options}
        taskLabel={
          final
            ? page.ui?.finalTaskLabel || (isMvp
              ? "Краткое описание идеи"
              : "Краткое описание задачи")
            : "Комментарий"
        }
        taskPlaceholder={
          final
            ? page.ui?.finalTaskLabel || (isMvp
              ? "Краткое описание идеи"
              : "Краткое описание задачи")
            : "Комментарий"
        }
        submitLabel={
          page.key === "mobile"
            ? slot === "estimate"
              ? "Рассчитать приложение за 1 день"
              : "Обсудить приложение"
            : final
              ? "Отправить заявку"
              : "Получить расчет бесплатно"
        }
        consentText={
          (page.key === "android" || isIntegration || isPublishing)
            ? "Даю согласие на обработку персональных данных в соответствии с "
            : undefined
        }
        consentLinkLabel={
          page.key === "android" || isIntegration || isPublishing ? "политикой конфиденциальности" : undefined
        }
      />
      {final && page.key !== "android" && !isIntegration && !isPublishing && (
        <p className="mobile-consent-note">
          Нажимая кнопку, вы соглашаетесь с{" "}
          <SiteLink href="/privacy/">
            политикой обработки персональных данных
          </SiteLink>
        </p>
      )}
    </div>
  );
}

function scrollToSection(event) {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const hash = event.currentTarget.getAttribute("href");
  const target = document.getElementById(hash.slice(1));
  if (!target) return;
  event.preventDefault();
  window.history.replaceState(window.history.state, "", hash);
  target.scrollIntoView({
    behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
      ? "auto"
      : "smooth",
    block: "start",
  });
}

function Cta({ children, page }) {
  return (
    <a
      className="btn btn-primary mobile-cta"
      onClick={scrollToSection}
      href={`#${page.key}-lead`}
    >
      {children}
      <ArrowForwardIcon aria-hidden="true" />
    </a>
  );
}

function PublishingStores({ groups }) {
  const icons = [AppleIcon, PlayArrowIcon, StorefrontIcon];
  return (
    <div className="publishing-stores">
      <div className="publishing-store-grid">
        {groups.slice(0, 3).map((group, i) => {
          const Icon = icons[i];
          const href = relatedPaths[group.title] || null;
          const Tag = href ? SiteLink : "article";
          return (
            <Tag className="publishing-store" key={group.title} {...(href ? {href} : {})}>
              <Icon aria-hidden="true" />
              <h3>{group.title}</h3>
              {group.blocks.map((block, j) => <Block block={block} key={j} />)}
              {href && <span className="publishing-store-more">{group.title === "Публикация в App Store" ? "Публикация приложения в App Store" : "Подробнее"} <ArrowForwardIcon aria-hidden="true" /></span>}
            </Tag>
          );
        })}
      </div>
      {groups.slice(3).map(group => (
        <div className="publishing-store-other" key={group.title}>
          <h3>{group.title}</h3>
          {group.blocks.map((block, i) => <Block block={block} key={i} />)}
        </div>
      ))}
    </div>
  );
}

function SourceSection({ section, page, number }) {
  const { intro, groups } = splitGroups(section.blocks);
  const title = section.title;
  const faq = title === "Ответы на частые вопросы";
  const reviews = title.startsWith("Отзывы");
  const stores = (page.key === "publishing" && title === "Где мы публикуем приложения") || (page.key === "aso" && title === "Где мы продвигаем приложения");
  const types = /^Какие (?:MVP|iOS|Android|интеграции)/.test(title) || (["google-play", "app-store", "rustore", "aso"].includes(page.key) && title.startsWith("Какие приложения мы публикуем"));
  const related = title === "Похожие услуги";
  const timeline =
    title.startsWith("Как проходит") ||
    title.startsWith("Как мы создаём") ||
    title.startsWith("Что входит");
  const technology = title.startsWith("Технологии") || (["google-play", "app-store", "rustore", "aso"].includes(page.key) && title.startsWith("Требования"));
  const pricing =
    title.startsWith("Стоимость") || title.startsWith("Сколько стоит");
  const final = title.startsWith("Заказать");
  const mainTypes = title === "Какие приложения мы разрабатываем";
  const result = title === "Что вы получаете в итоге";
  const formats = title === "Форматы сотрудничества";
  const grid =
    technology ||
    formats ||
    result ||
    title.startsWith("Подход") ||
    title.startsWith("Поддержка") ||
    mainTypes;
  const dark = technology || pricing || final;
  let reviewLead = null;
  const reviewPairs = [];
  if (reviews) {
    intro.forEach((block) => {
      if (block.text?.startsWith("**"))
        reviewPairs.push({ name: block.text, quote: null });
      else if (block.text?.startsWith("«") && reviewPairs.length)
        reviewPairs[reviewPairs.length - 1].quote = block.text;
      else reviewLead = block;
    });
  }
  return (
    <section
      className={`mobile-section ${["integrations", "publishing", "google-play", "app-store", "rustore", "aso"].includes(page.key) && title.startsWith("Как проходит") ? "integration-process" : ""} ${dark ? "mobile-section-dark" : ""}`}
      id={section.id}
      data-source-section={section.id}
    >
      <div className="container">
        <div className="mobile-section-heading">
          <span className="eyebrow">{String(number).padStart(2, "0")} /</span>
          <h2>{title}</h2>
        </div>
        {related ? (
          <div className="mobile-related">
            {intro
              .filter((b) => b.type === "list")
              .flatMap((b) => b.items)
              .map((text, i) => {
                const label = text.replace(/\s*\(.*$/, "");
                const explicit = text.match(/\]\((https?:[^)]+)\)/)?.[1];
                if (!explicit && !relatedPaths[label]) return <div className="mobile-related-pending" key={i}><span>{label}</span></div>;
                return (
                  <SiteLink key={i} href={explicit || relatedPaths[label]}>
                    <span>{label}</span>
                    <ArrowForwardIcon aria-hidden="true" />
                  </SiteLink>
                );
              })}
          </div>
        ) : reviews ? (
          <ReviewsCarousel className="mobile-reviews">
            {reviewPairs.map((review, i) => (
              <figure key={i}>
                <blockquote>{review.quote}</blockquote>
                <figcaption>
                  <MobileInline text={review.name} />
                </figcaption>
              </figure>
            ))}
          </ReviewsCarousel>
        ) : (
          <div className="mobile-section-intro">
            {intro.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </div>
        )}
        {groups.length > 0 && (stores ? <PublishingStores groups={groups} /> : (
          <div
            className={
              faq || types
                ? "mobile-accordions"
                : timeline
                  ? "mobile-timeline"
                  : grid
                    ? `mobile-grid ${formats ? "mobile-grid-formats" : result ? "mobile-grid-results" : ""}`
                    : "mobile-groups"
            }
          >
            {groups.map((group, i) => {
              const contents = (
                <>
                  {group.blocks.map((block, j) => (
                    <Block key={j} block={block} />
                  ))}
                  {types && (
                    <>
                      <Cta page={page}>Узнать стоимость</Cta>
                      {page.key === "mvp" &&
                        group.title.includes("Telegram") && (
                          <SiteLink
                            className="mobile-text-link"
                            href="/services/mini-apps/"
                          >
                            Разработка Mini App
                          </SiteLink>
                        )}
                    </>
                  )}
                  {formats && <Cta page={page}>Уточнить условия</Cta>}
                  {group.title === "Получить расчет" ||
                  group.title === "Получить оценку проекта" ? (
                    <ProjectForm page={page} slot="estimate" />
                  ) : null}
                </>
              );
              if (faq || types)
                return (
                  <details
                    className={`mobile-disclosure ${faq ? "faq-item" : ""}`}
                    key={group.title}
                    open={i === 0}
                  >
                    <summary className={faq ? "faq-q" : ""}>
                      <h3>{group.title}</h3>
                      <span aria-hidden="true">+</span>
                    </summary>
                    <div className="mobile-disclosure-content">{contents}</div>
                  </details>
                );
              const Icon = [CodeIcon, StorageIcon, HubIcon, TaskAltIcon][i % 4];
              return (
                <div
                  key={group.title}
                  className={`mobile-group ${group.blocks.some((b) => b.type === "table") && technology ? "mobile-group-wide" : ""}`}
                >
                  {(grid || timeline) && (
                    <div className="mobile-group-marker" aria-hidden="true">
                      {timeline ? (
                        <>
                          {String(i + 1).padStart(2, "0")}
                          <ArrowForwardIcon />
                        </>
                      ) : (
                        <Icon />
                      )}
                    </div>
                  )}
                  <h3>
                    {mainTypes ? (
                      <SiteLink href={typeLinks[group.title]}>
                        {group.title}
                        <ArrowForwardIcon aria-hidden="true" />
                      </SiteLink>
                    ) : (
                      group.title
                    )}
                  </h3>
                  {contents}
                </div>
              );
            })}
          </div>
        ))}
        {technology && !["publishing", "google-play", "app-store", "rustore", "aso"].includes(page.key) && (
          <div className="mobile-tech-strip" aria-label="Технологии">
            {(page.key === "integrations"
              ? ["REST", "SOAP", "GraphQL", "JSON", "OAuth"]
              : page.key === "mvp"
              ? ["React", "React Native", "TypeScript", "Telegram"]
              : page.key === "ios"
                ? ["React Native", "TypeScript", "Swift", "App Store"]
                : page.key === "android"
                  ? ["React Native", "Kotlin", "Android Studio", "TypeScript"]
                  : ["React Native", "TypeScript", "Flutter", "API"]
            ).map((label, i) => {
              const TechIcon =
                label === "App Store" || label === "Swift"
                  ? AppleIcon
                  : label === "Android Studio" || label === "Kotlin"
                    ? AndroidIcon
                    : [CodeIcon, HubIcon, StorageIcon, CodeIcon][i % 4];
              return (
                <div key={label}>
                  <TechIcon aria-hidden="true" />
                  <span>{label}</span>
                </div>
              );
            })}
          </div>
        )}
        {timeline && (!["integrations", "publishing", "google-play", "app-store", "rustore", "aso"].includes(page.key) || title.startsWith("Что входит")) && <Cta page={page}>{page.ui?.timelineCta || "Начать проект"}</Cta>}
        {result && page.key !== "mobile" && (
          <Cta page={page}>
            {page.ui?.resultCta || (page.key === "integrations"
              ? "Заказать интеграцию"
              : page.key === "mvp"
              ? "Заказать MVP"
              : `Заказать ${page.key === "ios" ? "iOS" : "Android"}-приложение`)}
          </Cta>
        )}
        {title.startsWith("Какие задачи") && (
          <Cta page={page}>
            {page.ui?.tasksCta || (page.key === "integrations"
              ? "Обсудить интеграцию — FATALITY"
              : page.key === "mvp"
              ? "От идеи до MVP — FATALITY"
              : page.key === "ios"
                ? "От идеи до разработки — FATALITY"
                : "От идеи до приложения — FATALITY")}
          </Cta>
        )}
        {number === 1 && page.key !== "mobile" && (
          <div className="mobile-trust">
            {[
              TaskAltIcon,
              ScheduleIcon,
              VerifiedUserIcon,
              SupportAgentIcon,
            ].map((Icon, i) => (
              <div key={i}>
                <Icon aria-hidden="true" />
                <span>
                  {
                    [
                      "договор с фиксированной ценой",
                      "сроки по этапам",
                      "гарантия 3 месяца",
                      "поддержка после запуска",
                    ][i]
                  }
                </span>
              </div>
            ))}
          </div>
        )}
        {reviews && (
          <>
            {reviewLead && <Block block={reviewLead} />}
            {page.ui?.reviewLead && <p>{page.ui.reviewLead}</p>}
            {page.key === "integrations" && <p>Хотите такой же результат? Оставьте заявку — разберем ваши системы.</p>}
            {page.key === "ios" && (
              <p>
                Хотите такой же результат? Оставьте заявку — разберем ваш
                проект.
              </p>
            )}
            {page.key === "android" && (
              <p>
                Хотите такой же результат? Оставьте заявку — разберем вашу
                задачу.
              </p>
            )}
            <ProjectForm page={page} slot="reviews" />
          </>
        )}
        {final && <ProjectForm page={page} slot="final" final />}
        {title.startsWith("Кейсы") && (
          <p className="mobile-pending" data-source-gap="case">
            {/* TODO(brand-redesign): add source text for mobile case, URL and original image */}
            Кейс готовится к публикации
          </p>
        )}
      </div>
    </section>
  );
}

export default function MobileDevelopmentPage({ pageKey = "mobile" }) {
  const page = mobilePages[pageKey];
  const Icon =
    pageKey === "aso"
      ? TrendingUpIcon
      : pageKey === "google-play"
      ? PlayArrowIcon
      : ["publishing", "rustore"].includes(pageKey)
      ? StorefrontIcon
      : pageKey === "integrations"
      ? HubIcon
      : ["ios", "app-store"].includes(pageKey)
      ? AppleIcon
      : pageKey === "android"
        ? AndroidIcon
        : CodeIcon;
  return (
    <RedesignPage>
      <PageMeta
        title={page.title}
        description={page.description}
        structuredData={page.structuredData}
      />
      <RedesignHeader />
      <main className="mobile-development">
        <section className="mobile-hero">
          <div className="container">
            <nav className="mobile-breadcrumbs" aria-label="Хлебные крошки">
              <SiteLink href="/">Главная</SiteLink>
              <span>/</span>
              <SiteLink href={page.ui?.breadcrumbParent?.href || "/services/mobile/"}>
                {page.ui?.breadcrumbParent?.label || "Разработка приложений"}
              </SiteLink>
              {pageKey !== "mobile" && (
                <>
                  <span>/</span>
                  <span>
                    {page.ui?.breadcrumb || (pageKey === "mvp"
                      ? "MVP"
                      : pageKey === "ios"
                        ? "iOS"
                        : pageKey === "integrations" ? "Интеграции" : "Android")}
                  </span>
                </>
              )}
            </nav>
            <div className="eyebrow cyan">
              <Icon aria-hidden="true" /> FATALITY /{" "}
              {pageKey === "mobile" ? "MOBILE" : pageKey.toUpperCase()}
            </div>
            <h1>{page.h1}</h1>
            <div className="mobile-hero-body">
              {page.intro.map((block, i) => (
                <Block key={i} block={block} />
              ))}
            </div>
            <Cta page={page}>
              {page.ui?.heroCta || (pageKey === "integrations"
                ? "Заказать интеграцию"
                : pageKey === "mobile"
                ? "Обсудить приложение"
                : pageKey === "mvp"
                  ? "Заказать MVP"
                  : `Заказать ${pageKey === "ios" ? "iOS" : "Android"}-приложение`)}
            </Cta>
          </div>
        </section>
        <nav className="mobile-toc container" aria-label="Содержание страницы">
          <details>
            <summary>
              Содержание страницы <span>{page.sections.length} разделов</span>
            </summary>
            <ol>
              {page.sections.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`} onClick={scrollToSection}>
                    {section.title}
                  </a>
                </li>
              ))}
            </ol>
          </details>
        </nav>
        {page.sections.map((section, i) => (
          <SourceSection
            key={section.id}
            section={section}
            number={i + 1}
            page={page}
          />
        ))}
        {pageKey === "mobile" && (
          <section className="mobile-section mobile-section-dark">
            <div className="container">
              <h3>Получить оценку проекта</h3>
              <ProjectForm page={page} slot="final" />
            </div>
          </section>
        )}
      </main>
      <RedesignFooter />
    </RedesignPage>
  );
}
